import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { Newspaper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_DE = ["","Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const MONTHS_EN = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
const COLORS = ["#2C5E4E","#8B5E3C","#3B6B8C","#6B4E71","#4E7B5E","#7B5E4E"];

export default function NewslettersPage() {
  const { language } = useLanguage();
  const [newsletters, setNewsletters] = useState([]);
  const months = language === "de" ? MONTHS_DE : MONTHS_EN;

  useEffect(() => {
    axios.get(`${API}/public/newsletters`).then(r => setNewsletters(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />
      <main className="px-6 md:px-12 py-12 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Newspaper className="h-6 w-6 text-forest-700" />
          <h1 className="font-outfit text-3xl sm:text-4xl font-medium text-forest-900">
            {language === "de" ? "Alle Newsletters" : "All Newsletters"}
          </h1>
        </div>

        {newsletters.length === 0 ? (
          <p className="text-stone-500 text-center py-12">{language === "de" ? "Noch keine Newsletters" : "No newsletters yet"}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {newsletters.map((nl, idx) => (
              <Link key={nl.id} to={`/newsletter/${nl.id}`} className="group" data-testid={`nl-list-${nl.id}`}>
                <div className="rounded-2xl overflow-hidden shadow-sm border border-stone-200 transition-all group-hover:-translate-y-1 group-hover:shadow-lg">
                  {nl.image_url ? (
                    <div className="h-44 overflow-hidden">
                      <img src={nl.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="h-44 flex items-center justify-center" style={{ backgroundColor: nl.cover_color || COLORS[idx % COLORS.length] }}>
                      <div className="text-center text-white">
                        <p className="text-4xl font-outfit font-bold">{months[nl.month]}</p>
                        <p className="text-lg opacity-80">{nl.year}</p>
                      </div>
                    </div>
                  )}
                  <div className="bg-white p-5">
                    <p className="text-xs text-stone-400 mb-1">{months[nl.month]} {nl.year}</p>
                    <h3 className="font-outfit font-medium text-forest-900 text-base leading-snug group-hover:text-forest-700 transition-colors">
                      {language === "de" ? nl.title_de : nl.title_en}
                    </h3>
                    <p className="text-sm text-stone-500 mt-2 line-clamp-2">
                      {language === "de" ? nl.content_de : nl.content_en}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="py-6 px-6 md:px-12 border-t border-stone-200 mt-12">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <p className="text-stone-500 text-xs">© 2026 Universität Trier. Learning Brain Lab</p>
        </div>
      </footer>
    </div>
  );
}
